import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Phone, Mail, MapPin, ArrowRight, Plane } from "lucide-react";
import LogoText from "./LogoText";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import GTranslateWidget from "./GTranslateWidget";

const Footer = () => {
  const { getConfig } = useSiteConfig();
  
  return (
    <footer className="relative bg-gradient-to-br from-primary via-primary to-primary/95 text-primary-foreground overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-accent/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <LogoText useFooterLogo={true} className="h-16" />
            <p className="text-primary-foreground/70 text-sm leading-relaxed">
              {getConfig('footer_description', 'Your trusted airport transfer service in Punta Cana. Professional, reliable, and comfortable transportation 24/7.')}
            </p>
            <GTranslateWidget />
            <div className="flex space-x-3 mt-4">
              {getConfig('social_facebook')?.enabled && (
                <a 
                  href={getConfig('social_facebook')?.url || '#'} 
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-primary-foreground/70 hover:bg-secondary hover:text-white transition-all duration-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {getConfig('social_instagram')?.enabled && (
                <a 
                  href={getConfig('social_instagram')?.url || '#'} 
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-primary-foreground/70 hover:bg-secondary hover:text-white transition-all duration-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {getConfig('social_tiktok')?.enabled && (
                <a 
                  href={getConfig('social_tiktok')?.url || '#'} 
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-primary-foreground/70 hover:bg-secondary hover:text-white transition-all duration-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Twitter className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Quick Links</h3>
            <ul className="space-y-3">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About Us" },
                { to: "/transfers", label: "Transfers" },
                { to: "/traslados", label: "Destinations" },
                { to: "/contact", label: "Contact" },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-primary-foreground/70 hover:text-secondary transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight className="h-3 w-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Our Services</h3>
            <ul className="space-y-3">
              {[
                "Airport Transfers",
                "Hotel Transfers",
                "Private Transportation",
                "Group Transfers",
              ].map((service) => (
                <li key={service}>
                  <Link to="/transfers" className="text-primary-foreground/70 hover:text-secondary transition-colors text-sm flex items-center gap-2 group">
                    <Plane className="h-3 w-3 text-secondary/60" />
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <h3 className="font-bold text-lg uppercase tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Phone className="h-4 w-4 text-secondary" />
                </div>
                <div>
                  <p className="text-primary-foreground/90 font-medium">{getConfig('contact_phone', '+1 (809) 123-4567')}</p>
                  <p className="text-primary-foreground/50 text-xs">24/7 Available</p>
                </div>
              </li>
              <li className="flex items-start space-x-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="h-4 w-4 text-secondary" />
                </div>
                <div>
                  <p className="text-primary-foreground/90 font-medium">{getConfig('contact_email', 'info@smartpuntacanatransfer.com')}</p>
                  <p className="text-primary-foreground/50 text-xs">Email Support</p>
                </div>
              </li>
              <li className="flex items-start space-x-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="h-4 w-4 text-secondary" />
                </div>
                <div>
                  <p className="text-primary-foreground/90 font-medium">Punta Cana</p>
                  <p className="text-primary-foreground/50 text-xs">Dominican Republic</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-primary-foreground/50 text-sm">
              © {new Date().getFullYear()} Smart Punta Cana Transfer. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/terms" className="text-primary-foreground/50 hover:text-secondary transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-primary-foreground/50 hover:text-secondary transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
